import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { categories, articles } from "@/mocks/data";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || "redai-haidao");

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug);
  const categoryArticles = articles.filter((a) => a.category === activeSlug);

  return (
    <main className="min-h-screen pt-16">
      {/* Category Header */}
      <section className="relative w-full h-[320px] md:h-[380px] overflow-hidden">
        {currentCategory && (
          <>
            <img
              src={currentCategory.heroImageUrl}
              alt={currentCategory.name}
              title={currentCategory.name}
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/55"></div>
            <div className="relative z-10 w-full h-full flex flex-col items-center justify-center px-4">
              <span className="w-16 h-16 flex items-center justify-center rounded-full bg-white/15 backdrop-blur-sm text-white mb-4">
                <i className={`${currentCategory.icon} text-2xl`}></i>
              </span>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-white text-center mb-3">
                {currentCategory.name}
              </h1>
              <p className="text-white/70 text-sm md:text-base text-center max-w-xl">
                {currentCategory.description}
              </p>
              <p className="text-white/50 text-xs mt-2">
                共 {currentCategory.count} 篇攻略
              </p>
            </div>
          </>
        )}
      </section>

      {/* Category Tabs */}
      <section className="bg-background-50 border-b border-background-200/70 sticky top-16 z-40">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeSlug === cat.slug
                    ? "bg-primary-500 text-white"
                    : "bg-transparent text-foreground-600 hover:bg-background-100"
                }`}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`${cat.icon} text-xs`}></i>
                </span>
                {cat.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Breadcrumb + Articles */}
      <section className="bg-background-50">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: "目的地" },
              { label: currentCategory?.name || "" },
            ]}
          />

          {categoryArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-4">
              {categoryArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group bg-white rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200/50 transition-all cursor-pointer"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={article.cardImageUrl}
                      alt={article.title}
                      title={article.title}
                      className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      {article.tags.slice(0, 2).map((tag) => (
                        <span
                          key={tag}
                          className="bg-accent-100 text-accent-800 text-xs px-2 py-0.5 rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <h3 className="font-heading text-base font-bold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-500 line-clamp-2 mb-3 leading-relaxed">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-xs text-foreground-400">
                      <span>{article.author}</span>
                      <div className="flex items-center gap-3">
                        <span>{article.publishDate}</span>
                        <span>{article.readTime}阅读</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mx-auto mb-4">
                <i className="ri-file-list-3-line text-2xl"></i>
              </span>
              <p className="text-foreground-500 text-sm">该分类下暂无文章</p>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}